import { AxiosInstance, AxiosResponse } from 'axios'

import {
  Address,
  FindAddressItem,
  SearchDataEntry,
  VehicleKBA,
  VehicleVIN,
} from '../../../domain'
import { withGenericErrorHandling } from '../errors'

export interface LookupMakes {
  year: number
}

export interface LookupModels extends LookupMakes {
  make: string
}

export interface LookupVehicles extends LookupModels {
  model: string
}

export interface LookupClient {
  lookupVehicle: (vrn: string, profile?: string) => Promise<VehicleVIN | null>
  lookupVehicleMakes: (lookup: LookupMakes, profile?: string) => Promise<string[]>
  lookupVehicleModels: (lookup: LookupModels, profile?: string) => Promise<string[]>
  lookupVehicles: (lookup: LookupVehicles, profile?: string) => Promise<VehicleVIN[]>

  lookupVehicleSearch: (
    search: Record<string, string | number>,
    profile?: string,
  ) => Promise<VehicleKBA[]>

  searchData: (tableCode: string, searchTerm: string) => Promise<SearchDataEntry[]>
  getDataEntry: (tableCode: string, key: string) => Promise<SearchDataEntry>
  getAddress: (id: string) => Promise<Address>
  findAddresses: (
    text: string,
    options?: Partial<{ container: string; countries: string[] }>,
  ) => Promise<FindAddressItem[]>
}

export const createLookupClient = (
  getInstance: () => Promise<AxiosInstance>,
): LookupClient => ({
  lookupVehicle: (vrn, profile) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<VehicleVIN[]> = await instance.get(
        '/lookup/vehicles/vehicles/vrn',
        { params: { vrn, profile } },
      )

      const [vehicle] = response.data.map((it) => ({ ...it, vrn: vrn }))
      return vehicle ?? null
    }),

  lookupVehicles: (lookup, profile) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<VehicleVIN[]> = await instance.get(
        `/lookup/vehicles/vehicles`,
        {
          params: {
            ...lookup,
            profile,
          },
        },
      )
      return response.data
    }),

  lookupVehicleMakes: (lookup, profile) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<string[]> = await instance.get(
        `/lookup/vehicles/makes`,
        { params: { ...lookup, profile } },
      )
      return response.data
    }),

  lookupVehicleModels: (lookup, profile) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<string[]> = await instance.get(
        `/lookup/vehicles/models`,
        { params: { ...lookup, profile } },
      )
      return response.data
    }),

  lookupVehicleSearch: (search, profile) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<VehicleKBA[]> = await instance.get(
        `/lookup/vehicles`,
        {
          params: { ...search, profile },
        },
      )
      return response.data.map((it) => ({ ...search, ...it }))
    }),

  searchData: (tableCode, searchTerm) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const response: AxiosResponse<SearchDataEntry[]> = await instance.get(
        `/lookup/data/${tableCode}`,
        {
          params: { searchTerm },
        },
      )
      return response.data
    }),

  getDataEntry: (tableCode, key) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const encodedKey = encodeURIComponent(key)
      const response: AxiosResponse<SearchDataEntry> = await instance.get(
        `/lookup/data/${tableCode}/${encodedKey}`,
      )
      return response.data
    }),

  getAddress: (id) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const service = CONFIG.locationService
      const response: AxiosResponse<Address> = await instance.get(
        `/lookup/address/${encodeURIComponent(id)}`,
        {
          params: { service },
        },
      )
      return response.data
    }),

  findAddresses: (text, options = {}) =>
    withGenericErrorHandling(async () => {
      const instance = await getInstance()
      const service = CONFIG.locationService
      const response: AxiosResponse<FindAddressItem[]> = await instance.get(
        `/lookup/addresses`,
        {
          params: { service, text, ...options },
          paramsSerializer: {
            indexes: null,
          },
        },
      )
      return response.data
    }),
})
